import React, { useEffect, useState } from "react";
import './App.css';
import { baseUrl } from '../shared';
import Login from '../components/login';
import FirstTab from "../components/tab-components/FirstTab";
import SafetyEssentialsUpload from "../components/tab-components/SafetyEssentialsUpload";
import Search from "../components/search";
import Timesheet from "./edit/timesheetAsset";
import Edit from './edit/editAsset';
import { InspectionForm, InspectionDetailModal } from './inspection';
import RepairForm from "../components/repairs/repairForm";
import RepairView from "../components/repairs/repairView";
import WorkshopJobModal from "../components/workshop/WorkshopJobModal";
import {
  Box,
  Button,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  TableHead,
  Tooltip,
  InputLabel,
  Typography,
  Menu,
  Divider,
  ListItemIcon,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { StatusChip, AssetIdentity } from "../components/ui";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpIcon from "@mui/icons-material/Help";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import BuildIcon from "@mui/icons-material/Build";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PartsUsedModal from "../components/PartsUsedModal";
import OfflineCheckoutModal from "../components/pwa/OfflineCheckoutModal";
import DefectChatModal from "../components/defects/DefectChatModal";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import LanguageIcon from "@mui/icons-material/Language";
import moment from "moment";
import { useLocation } from "react-router-dom";

// Pick a file-type icon from the attachment's name/extension so document links read at a glance.
function FileTypeIcon({ name }) {
  const iconProps = { sx: { fontSize: 18, flexShrink: 0, color: 'var(--primary)' } };
  // URLs / web links — match before extension parsing (a URL may have no real file extension).
  if (/^https?:\/\//i.test(name || '')) return <LanguageIcon {...iconProps} />;
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return <PictureAsPdfIcon {...iconProps} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'svg', 'tif', 'tiff'].includes(ext)) return <ImageIcon {...iconProps} />;
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'm4v', 'mpeg', 'mpg', '3gp'].includes(ext)) return <VideoFileIcon {...iconProps} />;
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma'].includes(ext)) return <AudioFileIcon {...iconProps} />;
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FolderZipIcon {...iconProps} />;
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return <SlideshowIcon {...iconProps} />;
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return <TableChartIcon {...iconProps} />;
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) return <DescriptionIcon {...iconProps} />;
  if (['html', 'htm', 'url', 'webloc'].includes(ext)) return <LanguageIcon {...iconProps} />;
  return <InsertDriveFileIcon {...iconProps} />;
}

// Document link: icon + clean (non-underlined) label, readable in light and dark mode.
// Pass either onClick (for proxied in-app open) or href (for direct links).
// isLink: the row is a saved web link (not an uploaded file) — force the web icon (its name is a label,
// not a filename, so FileTypeIcon can't infer it).
function AttachmentLink({ name, onClick, href, isLink }) {
  return (
    <a
      href={href || '#'}
      onClick={onClick}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      title={name}
      style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 6, width: '100%', minWidth: 0 }}
    >
      {isLink ? <LanguageIcon sx={{ fontSize: 18, flexShrink: 0, color: 'var(--primary)' }} /> : <FileTypeIcon name={name} />}
      <span style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}>{name}</span>
    </a>
  );
}

// Message count + unread badge shown on defect rows. Rows are clickable into the
// discussion thread; this is the visual cue that a thread exists / has news.
function DefectChatIndicator({ messageCount, unreadCount }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--muted)', flexShrink: 0 }}>
      <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
      <Typography variant="caption">{messageCount ?? 0}</Typography>
      {unreadCount > 0 && (
        <Box component="span" sx={{ background: 'var(--primary)', color: '#fff', borderRadius: '10px', px: 0.75, py: 0.1, fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
          {unreadCount} new
        </Box>
      )}
    </Box>
  );
}

function AssetView() {
  // Read the lookup params from the live location on every navigation. (Previously
  // parsed once at module load, so navigating Home → back to Assets via the top bar —
  // no full reload — kept showing the stale asset instead of the search panel.)
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const assetNoUrl = urlParams.get('assetNo');
  const catNoUrl = urlParams.get('catNo');
  // Workshop History is the last sub-section in the History dropdown, but only for
  // users who can see it — used to drive which sub-section rounds its bottom corners.
  const canSeeWorkshopHistory = localStorage.getItem('canViewWorkshopHistory') === '1' || localStorage.getItem('canManageWorkshop') === '1';
  const [asset, setAsset] = useState();
  const [editing, setEditing] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [addAttachment, setAddAttachment] = useState(false);
  var [inspectionHistory, setInspectionHistory] = useState();
  const [inspectionHistoryLoaded, setInspectionHistoryLoaded] = useState(false);
  const [generatingInspPdf, setGeneratingInspPdf] = useState(null);
  const [uploadIdForDelete, setUploadIdForDelete] = useState('');
  const [workshopHistory, setWorkshopHistory] = useState([]);
  const [workshopHistoryOpen, setWorkshopHistoryOpen] = useState(false);
  const [workshopHistoryLoading, setWorkshopHistoryLoading] = useState(false);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [partsUsedOpen, setPartsUsedOpen] = useState(false);
  const [addDefect, setAddDefect] = useState(false);  
  const [resolveDefect, setResolveDefect] = useState(false);
  // Which detail section the segmented control is showing (defects | documents | history). null = default to first available.
  const [activeSection, setActiveSection] = useState(null);
  // Desktop only: each top-level column (Documents / History / Defects) is collapsed until its heading is clicked.
  const [docsColOpen, setDocsColOpen] = useState(false);
  const [histColOpen, setHistColOpen] = useState(false);
  const [defectsColOpen, setDefectsColOpen] = useState(false);
  // >= md is treated as the desktop 3-column layout; below that keeps the segmented tabs.
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [openDefectLoaded, setOpenDefectLoaded] = useState(false);
  var [openDefects, setOpenDefects] = useState();
  const [closedDefectLoaded, setClosedDefectLoaded] = useState(false);
  var [closedDefects, setClosedDefects] = useState();
  const [priority, setPriority] = React.useState('');
  const [defectComments, setDefectComments] = React.useState('');
  const [defectReportedBy, setDefectReportedBy] = React.useState('');
  const [defectResolution, setDefectResolution] = React.useState('');
  const [toastMessage, setToastMessage] = useState('');
  var [selectedDefectId, setSelectedDefectId] = useState('');
  // Which defect's discussion thread is open (null = chat modal closed).
  const [chatDefectId, setChatDefectId] = useState(null);
  // Optional photo picked in the Add Defect dialog; uploaded to the new
  // defect's thread right after creation succeeds.
  const [defectPhoto, setDefectPhoto] = useState(null);
  const [certificateDocumentLoaded, setCertificatesLoaded] = useState(false);
  const [safetyEssentialsLoaded, setSafetyEssentialsLoaded] = useState(false);
  const [partsAttachmentsLoaded, setPartsLoaded] = useState(false);
  const [photoAttachmentsLoaded, setPhotosLoaded] = useState(false);
  const [timesheetsLoaded, setTimesheetsLoaded] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);
  var [timesheetList, setTimesheetList] = useState();
  var [Certificates, setCertificates] = useState();
  var [safetyEssentials, setSafetyEssentials] = useState();
  var [Parts, setParts] = useState();
  var [Photos, setPhotos] = useState();
  const [contactOpen, setcontactOpen] = React.useState(false)
  const [mode, setMode] = useState("view");
  const [repairOpen, setRepairOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [safetyUploadOpen, setSafetyUploadOpen] = useState(false);
  const [advancedInspectOpen, setAdvancedInspectOpen] = useState(false);
  const [checkoutDetailOpen, setCheckoutDetailOpen] = useState(false);
  const [inspDetailOpen, setInspDetailOpen] = useState(false);
  const [inspDetailId, setInspDetailId] = useState(null);
  const [repairListVisible, setRepairListVisible] = useState(false);
  const [repairs, setRepairs] = useState([]);
  const [selectedRepairId, setSelectedRepairId] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  
  const [toastState, setToastState] = React.useState({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const { vertical, horizontal, toastOpen } = toastState;

  const handleChange = (event) => {
    setPriority(event.target.value);
  };

  const handleComments = (event) => {
    setDefectComments(event.target.value);
  };

  const handleReportedBy = (event) => {
    setDefectReportedBy(event.target.value);
  };

  const handleResolution = (event) => {
    setDefectResolution(event.target.value);
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastState({ ...toastState, toastOpen: false})
  }

  

  function getOpenRepairs() {   
    const url = baseUrl + 'repair/openRepairs/';
    fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage?.getItem('username'),
        'bToken': localStorage?.getItem('bToken'),
        'Assetid': asset?.assetId,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const repairId = data?.length > 0 ? data[0]?.repairId : null;

        setSelectedRepairId({
          assetId: asset?.assetId,
          repairId,
        });

        setMode("edit");
        handleRepairVisible();
      })
      .catch((err) => {
        console.error("Error fetching open repairs:", err);
      });
}


  useEffect(() => {
    if (asset?.assetId) {
      const url = baseUrl + 'repair/repairs/';
      fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage?.getItem('username'),
            'bToken': localStorage?.getItem('bToken'),
            'Assetid': asset?.assetId
        }})
        .then((res) => res.json())
        .then((data) => setRepairs(data));
    }
  }, [asset?.assetId]);

  useEffect(()=> {
    console.log('Asset ',asset);
    console.log('Edit State:',editing);
    console.log('Inspecting State ',inspecting);
  });
  
  const lookUpAssetNo = async () => {
      const url = baseUrl + assetNoUrl;
      await fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage?.getItem('username'),
            'bToken': localStorage?.getItem('bToken')
        }})
      .then((response) => response.json())
      .then((data) => {
        if(data[0] === undefined){
          setToastMessage("No Asset Found");
          setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
        } else {
         setAsset(data[0]);
        }
      })
      .catch((err) => {
         console.log(err.message);
         setToastMessage("No Asset Found");
         setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
      });
  };

  const lookUpCatNo = async () => {
      const url = baseUrl + 'catNo/' +catNoUrl;
      await fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
        }})
      .then((response) => response.json())
      .then((data) => {
        if(data[0] === undefined){
          setToastMessage("No Asset Found");
          setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
        } else {
         setAsset(data[0]);
        }
      })
      .catch((err) => {
         console.log(err.message);
         setToastMessage("No Asset Found");
         setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
      });
  };

  useEffect(() => {
    if(catNoUrl !== null) {
      lookUpCatNo()
    } else if(assetNoUrl !== null) {
      lookUpAssetNo()
    } else {
      // No lookup params (e.g. landed on /assets via the top bar) — clear any
      // previously-loaded asset so the search panel shows instead of a stale one.
      setAsset(undefined)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetNoUrl, catNoUrl]);

  // Re-fetch just the current asset (updates the summary card after an inspection/edit)
  // without reloading the whole page. Prefers a live id over the original URL param.
  const refreshAsset = async () => {
    const id = asset?.assetId || assetNoUrl;
    const cat = asset?.catNo || catNoUrl;
    const url = id ? baseUrl + id : (cat ? baseUrl + 'catNo/' + cat : null);
    if (!url) return;
    try {
      const res = await fetch(url, { headers: { username: localStorage.getItem('username'), bToken: localStorage.getItem('bToken') } });
      const data = await res.json();
      if (data && data[0]) setAsset(data[0]);
    } catch (err) { console.log(err.message); }
  };

  function showPartsAttachments() {
      const url = baseUrl + 'attachmentList/parts/' + asset.catNo;
      fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
        }})
        .then((res) => {
          return res.json();
      }).then((json) => {
           setParts(json);
           setPartsLoaded(true);
      })
      .catch((err) => {
         console.log(err.message);
      });
    }

    function showCertificateAttachments() {
        const url = baseUrl + 'attachmentList/certificate/' + asset.catNo;
        fetch(url, {
          method: 'GET',
          headers: {
              'username': localStorage.getItem('username'),
              'bToken': localStorage.getItem('bToken')
          }})
          .then((res) => {
            return res.json();
        }).then((json) => {
             setCertificates(json);
             setCertificatesLoaded(true);
        })
        .catch((err) => {
           console.log(err.message);
        });
      }

    function showSafetyEssentialsList() {
             setSafetyEssentialsLoaded(true);
        const url = baseUrl + 'attachmentList/safetyEssentials/' + asset.catNo;
        fetch(url, {
          method: 'GET',
        })
          .then((res) => {
            return res.json();
        }).then((json) => {
             setSafetyEssentials(json);
             setSafetyEssentialsLoaded(true);
        })
        .catch((err) => {
           console.log(err.message);
        });
      }

      function showPhotoAttachments() {
          const url = baseUrl + 'attachmentList/photo/' + asset.catNo;
          fetch(url, {
            method: 'GET',
            headers: {
                'username': localStorage.getItem('username'),
                'bToken': localStorage.getItem('bToken')
            }})
            .then((res) => {
              return res.json();
          }).then((json) => {
               setPhotos(json);
               setPhotosLoaded(true);
          })
          .catch((err) => {
             console.log(err.message);
          });
        }

    function loadWorkshopHistory() {
      if (workshopHistoryOpen) { setWorkshopHistoryOpen(false); return; }
      setWorkshopHistoryLoading(true);
      setWorkshopHistoryOpen(true);
      fetch(baseUrl + 'workshop/asset-history/' + asset.catNo, {
        headers: { username: localStorage.getItem('username'), bToken: localStorage.getItem('bToken') }
      })
        .then(r => r.json())
        .then(d => { setWorkshopHistory(Array.isArray(d) ? d : []); setWorkshopHistoryLoading(false); })
        .catch(() => setWorkshopHistoryLoading(false));
    }

    function showInspectionHistory() {
      const url = baseUrl + 'inspectHistory/' + asset.assetId;
      fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
        }})
        .then((res) => {
          return res.json();
      }).then((json) => {
           setInspectionHistory(json);
           setInspectionHistoryLoaded(true);
      })
      .catch((err) => {
         console.log(err.message);
      });
    }

    // Same PDF report generation as the /inspection screen, for history rows
    // backed by a detailed inspection record.
    async function generateInspectionPdf(workshopInspectionId) {
      setGeneratingInspPdf(workshopInspectionId);
      try {
        const res = await fetch(baseUrl + 'inspection/results/' + workshopInspectionId + '/report', {
          method: 'POST',
          headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
          }
        });
        const data = await res.json();
        if (data.proxyUrl) {
          const pdfRes = await fetch(baseUrl + data.proxyUrl, {
            headers: {
              'username': localStorage.getItem('username'),
              'bToken': localStorage.getItem('bToken')
            }
          });
          const blob = await pdfRes.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.fileName || 'Inspection Report.pdf';
          a.click();
          window.open(url, '_blank');
        }
      } catch (err) {
        console.log(err.message);
      } finally {
        setGeneratingInspPdf(null);
      }
    }

    function loadTimesheets() {
      const url = baseUrl+'timesheet/list/';
      fetch(url, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'username': localStorage.getItem('username'),
              'bToken': localStorage.getItem('bToken')
          },
            body: JSON.stringify(asset)})
        .then((res) => {
          return res.json();
      }).then((json) => {
           setTimesheetList(json);
           showTimesheetsList();
      })
      .catch((err) => {
         console.log(err.message);
      });
    }

    function showTimesheetsList() {
      if(timesheetsLoaded) {
        setTimesheetsLoaded(false)
      } else
      { setTimesheetsLoaded(true)}
    }
    

    function showRepairsList() {
      if(!repairListVisible) {
        setRepairListVisible(true)
      } else {
        setRepairListVisible(false)
      }
    }

    function handleContactShow() {
      if(contactOpen) {
        setcontactOpen(false)
      } else
      setcontactOpen(true)
    }

    function handleRepairVisible() {
      if(!repairOpen) {
        setRepairOpen(true)
      } else {
        setRepairOpen(false)
      }
    };

    function handleUploadVisible() {
      if(!uploadOpen) {
        setUploadOpen(true)
      } else {
        setUploadOpen(false)
      }
    };

    function handleSafetyUploadVisible() {
      if(!safetyUploadOpen) {
        setSafetyUploadOpen(true)
      } else {
        setSafetyUploadOpen(false)
      }
    }



    function addNewDefect() {
      setDefectPhoto(null)
      if(addDefect) {
        setAddDefect(false)
      } else
      { setAddDefect(true)}
    }

    // Upload the optional creation photo into the new defect's thread. The
    // defect itself is already created; a failed photo just gets a toast (it
    // can be re-attached from the discussion).
    async function uploadDefectCreationPhoto(defectId, file) {
      try {
        const formData = new FormData();
        formData.append('myFile', file);
        const res = await fetch(baseUrl + 'defect/' + defectId + '/attachment', {
          method: 'POST',
          headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
          },
          body: formData,
        });
        if (!res.ok) throw new Error('upload failed');
      } catch (err) {
        setToastMessage('Defect created, but the photo upload failed. You can attach it again from the defect discussion.');
        setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
      }
    }

    function markDefectResolved(e) {
      if(resolveDefect) {
        setResolveDefect(false)
      } else
      { setSelectedDefectId(e)
        setResolveDefect(true)}
    }

    function openLoginPage() {
      if(openLogin) {
        setOpenLogin(false)
      } else {    
        setOpenLogin(true)}
    }
    
    async function submitNewDefect(e) {
      if(priority === '' || defectComments === ''){
        console.log('fill in the fields!')
      } else {
        e.preventDefault();
        const defectInfo = {
          assetId: asset.assetId,
          reportedBy: defectReportedBy,
          defectComment: defectComments,
          priority: priority,
        };
        const url = baseUrl + 'defect/new/';
        await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'username': localStorage.getItem('username'),
                'bToken': localStorage.getItem('bToken')
            },
            body: JSON.stringify(defectInfo),
        })
            .then((response) => response.json())
              .then(async (data) => {
                console.log(data[0])
                if( data[0].status === 200) {
                  setAddDefect(false)
                if (defectPhoto && data[0].defectId) {
                  await uploadDefectCreationPhoto(data[0].defectId, defectPhoto);
                }
                setDefectPhoto(null)
                showOpenDefects();
                setToastMessage(data[0].message);
                setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
                } else if (data[0].status === 401) {
                  setToastMessage(data[0].message);
                  setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
                }
              })
              .catch((err) => {
                setToastMessage(err.message);
                setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
              }); 
        console.log('priority: ',priority)
      console.log('comments: ',defectComments)
      setPriority('')
      setDefectComments('')
      setDefectReportedBy('')
      }
    }

    async function resolveExistingDefect(e) {
      if(defectResolution === ''){
        console.log('fill in the fields!')
      } else {
        e.preventDefault();
        const defectInfo = {
          assetId: asset.assetId,
          defectId: selectedDefectId,
          resolvedComment: defectResolution,
        };
        const url = baseUrl + 'defect/resolved/';
        await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'username': localStorage.getItem('username'),
                'bToken': localStorage.getItem('bToken')
            },
            body: JSON.stringify(defectInfo),
        })
            .then((response) => response.json())
              .then((data) => {
                console.log(data[0])
                if( data[0].status === 200) {
                  setResolveDefect(false)
                setToastMessage(data[0].message);
                setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
                showOpenDefects();
                if (closedDefectLoaded) showClosedDefects();
                } else if (data[0].status === 401) {
                  setToastMessage(data[0].message);
                  setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
                }
              })
              .catch((err) => {
                setToastMessage(err.message);
                setToastState({ ...toastState, toastOpen: true, vertical: 'top', horizontal: 'center' })
              }); 
        console.log('priority: ',priority)
      console.log('comments: ',defectComments)
      setPriority('')
      setDefectComments('')
      setDefectReportedBy('')
      }
    }

    function showOpenDefects() {
      const url = baseUrl + 'defect/open/'+asset.assetId;
      fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
        }
      })
        .then((res) => {
          return res.json();
      }).then((json) => {
            console.log('open defects:',json);
           setOpenDefects(Array.isArray(json) ? json : []);
           setOpenDefectLoaded(true);
      })
      .catch((err) => {
         console.log(err.message);
      });
    }

    function successUpload() {
      setShowSnackbar(true);
      setUploadOpen(false)
      // Partial refresh: re-fetch only the document lists that are currently open
      if (certificateDocumentLoaded) showCertificateAttachments();
      if (partsAttachmentsLoaded) showPartsAttachments();
      if (photoAttachmentsLoaded) showPhotoAttachments();
    }

    function successSafetyUpload() {
      setShowSnackbar(true);
      setSafetyUploadOpen(false)
      if (safetyEssentialsLoaded) showSafetyEssentialsList();
    }

    function hideOpenDefects() {
      setOpenDefects();
      setOpenDefectLoaded(false)
    }

    function showClosedDefects() {
      const url = baseUrl + 'defect/closed/'+asset.assetId;
      fetch(url, {
        method: 'GET',
        headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken')
        }
      })
        .then((res) => {
          return res.json();
      }).then((json) => {
           setClosedDefects(Array.isArray(json) ? json : []);
           setClosedDefectLoaded(true);
      })
      .catch((err) => {
         console.log(err.message);
      });
    }

    function hideClosedDefects() {
      setClosedDefects();
      setClosedDefectLoaded(false)
    }

 
    const [open, setOpen] = React.useState(false);
    const [timesheetOpen, setTimesheetOpen] = React.useState(false);
    const [foundTimesheet, setFoundTimesheet] = useState({});
    

    const handleClickOpen = uploadId => {
      setUploadIdForDelete(uploadId)
      setOpen(true);
    };

  const handleAttachmentOpen = async (proxyUrl) => {
    const res = await fetch(baseUrl + proxyUrl, {
      headers: {
        username: localStorage.getItem('username'),
        bToken: localStorage.getItem('bToken'),
      },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setUploadIdForDelete('')
    setOpen(false);
  };

  const handleTimesheetShow = () => {
    if (timesheetOpen) {
      setTimesheetOpen(false)  
  } else {
    setTimesheetOpen(true)
  }};

    function delAttachment() {
      const url = baseUrl + 'uploadDel';
      fetch(url, {
        method: 'POST',
        headers: {
            'username': localStorage.getItem('username'),
            'bToken': localStorage.getItem('bToken'),
            'uploadId': uploadIdForDelete
        }}) 
        .then((res) => {
          return res.json();
      }).then((json) => {
           setOpen(false)
           setUploadIdForDelete('')
      })
      .catch((err) => {
         console.log(err.message);
      });
    }

    function hideCertificateDocuments() {
      setCertificatesLoaded(false);
    }

    function hideSafetyEssentialsList() {
      setSafetyEssentialsLoaded(false);
    }

    function hidePartsDocuments() {
      setPartsLoaded(false);
    }

    function hidePhotoDocuments() {
      setPhotosLoaded(false);
    }

    function hideInspectionHistory() {
      setInspectionHistoryLoaded(false);
    }

  // --- Redesign helpers (Direction C) for the asset detail summary card ---
  const fmtDate = (d) => (d === null || d === undefined || d === '-' || d === '') ? '—' : moment(d).format('DD-MM-YYYY');
  const DetailField = ({ label, value, mono, children }) => (
    <Box>
      <Box sx={{ fontSize: 10, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>{label}</Box>
      <Box
        className={mono ? 'aims-mono' : undefined}
        sx={{ fontSize: 13.5, color: 'var(--text)', mt: '2px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {children ?? (value === null || value === undefined || value === '' ? '—' : value)}
      </Box>
    </Box>
  );
  const KpiTile = ({ label, value, action }) => (
    <Box sx={{ flex: '1 1 30%', minWidth: 120, background: 'var(--surface-tint)', borderRadius: '10px', p: '11px 13px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
        <Box sx={{ fontSize: 10, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>{label}</Box>
        {action}
      </Box>
      <Box className="aims-mono" sx={{ fontSize: 14, color: 'var(--text)', mt: '2px' }}>{value}</Box>
    </Box>
  );

  // Soft HQ "out for field inspection" badge (Offline Inspection mode). Purely
  // informational — offline field edits and HQ metadata edits are disjoint sets,
  // so this is NEVER a block/gate. offlineCheckoutVisitId arrives via SELECT a.*
  // on the authenticated asset read (no backend change needed).
  const OfflineCheckoutBadge = () => {
    // Internal-only indicator: shown to schedule managers and inspectors (the
    // people for whom "someone is already inspecting this" is relevant). Never
    // customer-facing (also keeps it off the logged-out QR view). Clearing the
    // checkout stays canManageSchedule-only inside OfflineCheckoutModal.
    const canSeeCheckout =
      localStorage.getItem('canManageSchedule') === '1' ||
      localStorage.getItem('canInspectAsset') === '1' ||
      localStorage.getItem('canUseDetailedInspection') === '1';
    if (!canSeeCheckout) return null;
    if (!asset || asset.offlineCheckoutVisitId == null) return null;
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setCheckoutDetailOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCheckoutDetailOpen(true); } }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          mt: 0.75, px: 1, py: '3px', borderRadius: '8px',
          fontSize: 11.5, fontWeight: 600, lineHeight: 1.2,
          color: 'var(--warn, #92610a)',
          background: 'var(--warn-tint, rgba(245,166,35,0.14))',
          border: '1px solid var(--warn, rgba(245,166,35,0.4))',
          cursor: 'pointer',
          '&:hover': { background: 'var(--warn-tint, rgba(245,166,35,0.24))', borderColor: 'var(--warn, rgba(245,166,35,0.7))' },
        }}
        title={`Out for field inspection on visit #${asset.offlineCheckoutVisitId}${asset.offlineCheckedOutAt ? ' since ' + fmtDate(asset.offlineCheckedOutAt) : ''}. Click for details.`}
      >
        <CloudOffIcon sx={{ fontSize: 14 }} />
        Out for field inspection · visit #{asset.offlineCheckoutVisitId}
      </Box>
    );
  };

  // Action handlers used by the meatballs menu (extracted from the old action dropdowns).
  // The legacy simple inspection sheet has been deprecated — all inspections now go through the
  // advanced InspectionForm. It decides per-asset whether to show a checklist (when the item maps
  // to one) or a no-checklist Pass/Fail/NTI screen, so there is no longer a simple/choice fork here.
  const openAssetInspection = () => { setAdvancedInspectOpen(true); };

  const openTimesheetAndParts = () => {
    setFoundTimesheet({ ...foundTimesheet, catNo: asset.catNo, assetId: asset.assetId, customer: asset.customer, id: null, timeSpent: null, notes: null, workPerformedOn: null, status: 'WIP', jobNumber: '-', jobCard: asset.currentJobCard, purchaseOrder: asset.currentPurchaseOrder, lineItem: asset.currentLineItem, omnixJobCardId: null, omnixSyncStatus: null });
    handleTimesheetShow();
  };

  return (
    
    <div
      className="assetView"
      // The asset-detail view is a normal top-aligned document (its inner wrapper
      // handles max-width + centering), so escape the shared grid-centering rule for
      // that state. Left as-is (grid, vertically centered) for the search/scan/login
      // panels. Without this, the grid shrinks the card to its content width — so a
      // card with the 1MAINTENANCE hire-link table rendered full-width while one
      // without it shrank to ~570px.
      style={asset && !editing && !inspecting ? { display: 'block' } : undefined}
    >
      {/* ✅ Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          sx={{ width: "100%" }}
          onClose={() => setShowSnackbar(false)}
        >
          All files uploaded successfully 🎉
        </Alert>
      </Snackbar>
      {/* Detail behind the "Out for field inspection" chip (visit info + who has
          it checked out; schedule managers can force-clear the checkout). */}
      <OfflineCheckoutModal
        open={checkoutDetailOpen}
        visitId={asset?.offlineCheckoutVisitId}
        onClose={() => setCheckoutDetailOpen(false)}
        onCleared={refreshAsset}
      />
      <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="confirmDeleteTitle">
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirmDeleteDesc">
            Are you sure you want to delete the Attachment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={delAttachment}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>

          <React.Fragment>
      <Dialog
        open={repairOpen}
        onClose={handleRepairVisible}
        maxWidth="sm"
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent sx={{ p: mode === "edit" ? 0 : 2 }}>
              { mode === "view" ? (
                  <RepairView assetId ={asset?.assetId} repairId={selectedRepairId?.repairId} />
              ):null}
              { mode === "edit" ? (
                <RepairForm
                  assetId={asset?.assetId}
                  repairId={selectedRepairId?.repairId}
                  onSuccess={() => setRepairOpen(false)}
                  onCancel={() => selectedRepairId?.repairId ? setMode("view") : setRepairOpen(false)}
                />
              ):(null)}
        </DialogContent>
        {mode === "view" && (
          <DialogActions sx={{ borderTop: '1px solid var(--border)' }}>
            <Button onClick={handleRepairVisible} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Close</Button>
            {localStorage.getItem('canInspectAsset') === '1' ? (
              <Button variant="contained" onClick={() => setMode("edit")}>Edit Repair</Button>
            ) : null}
          </DialogActions>
        )}
      </Dialog>
    </React.Fragment>

          <React.Fragment>
      <Dialog
        open={uploadOpen}
        onClose={handleUploadVisible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>
        </DialogTitle>
        <DialogContent>
            <div style={{ maxWidth: "800px", margin: "auto" }}>
                  <FirstTab foundAsset={asset} onSuccess={() => successUpload()}/>
            </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadVisible}>Close</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>

          <React.Fragment>
      <Dialog
        open={safetyUploadOpen}
        onClose={handleSafetyUploadVisible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>
        </DialogTitle>
        <DialogContent>
            <div style={{ maxWidth: "800px", margin: "auto" }}>
                  <SafetyEssentialsUpload foundAsset={asset} onSuccess={() => successSafetyUpload()}/>
            </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSafetyUploadVisible}>Close</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>

    {/* Advanced inspection form — launched directly from asset view (replaces the deprecated simple sheet) */}
    <InspectionForm
      open={advancedInspectOpen}
      onClose={() => setAdvancedInspectOpen(false)}
      onSaved={() => { setAdvancedInspectOpen(false); refreshAsset(); if (inspectionHistoryLoaded) showInspectionHistory(); }}
      prefillAsset={asset ? { catNo: asset.catNo, customer: asset.customer, assetId: asset.assetId, item: asset.item, manufacturer: asset.manufacturer, model: asset.model, assetType: asset.item, firstTimeInspected: asset.firstTimeInspected } : null}
      companies={[]}
    />

    {/* Advanced inspection detail modal — read-only, opened from Asset History */}
    <InspectionDetailModal
      open={inspDetailOpen}
      onClose={() => setInspDetailOpen(false)}
      inspectionId={inspDetailId}
    />

    <WorkshopJobModal
      open={newJobOpen}
      onClose={() => setNewJobOpen(false)}
      job={null}
      onSaved={() => setNewJobOpen(false)}
      initialData={asset ? { customer: asset.customer, catNo: asset.catNo, assetId: asset.assetId } : undefined}
    />

    <React.Fragment>
      <Dialog
        open={contactOpen}
        onClose={!contactOpen}
      >
        <DialogTitle>
        Site Contact Information
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="showContactDetailsDesc">
            Contact Name: {asset ? asset.siteContact : ''} <br />
            Contact Email: {asset ? asset.siteContactEmail : ''} <br />
            Contact Phone: {asset ? asset.siteContactPhone : ''} <br />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContactShow}>OK</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>

    <React.Fragment>
      <Dialog
        open={timesheetOpen}
        onClose={handleTimesheetShow}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <Timesheet foundTimesheet={foundTimesheet} onCancel={handleTimesheetShow}/>
        </DialogContent>
      </Dialog>
    </React.Fragment>

    <PartsUsedModal open={partsUsedOpen} onClose={() => setPartsUsedOpen(false)} asset={asset} />

    <React.Fragment>
      <Dialog
        open={addDefect}
        onClose={addNewDefect}
      >
        <DialogTitle>Add New Defect</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select the Priority of the defect found and enter your issues found below.
          </DialogContentText>
          <br />
          <InputLabel id="priority">Select Priority *</InputLabel>
          <Select
            labelId="priority"
            id="priority"
            value={priority}
            label="priority"
            variant="standard"
            margin="dense"
            onChange={handleChange}
            >
              <MenuItem value={'Low'}>Low</MenuItem>
              <MenuItem value={'Medium'}>Medium</MenuItem>
              <MenuItem value={'High'}>High</MenuItem>
          </Select>
          <TextField
            autoFocus
            multiline
            required
            margin="dense"
            id="reportedBy"
            name="reportedBy"
            label="Reported By"
            fullWidth
            variant="standard"
            onChange={handleReportedBy}
          />
          <TextField
            autoFocus
            multiline
            required
            margin="dense"
            id="defectFound"
            name="defectFound"
            label="Defect Found"
            fullWidth
            variant="standard"
            onChange={handleComments}
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button component="label" variant="outlined" size="small" startIcon={<ImageIcon />} sx={{ textTransform: 'none' }}>
              {defectPhoto ? 'Change Photo' : 'Attach Photo (optional)'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = '';
                  if (file) setDefectPhoto(file);
                }}
              />
            </Button>
            {defectPhoto && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                  {defectPhoto.name}
                </Typography>
                <IconButton size="small" onClick={() => setDefectPhoto(null)} aria-label="remove photo">
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
          <Button onClick={addNewDefect} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Cancel</Button>
          <Button onClick={submitNewDefect} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>

    <React.Fragment>
      <Dialog
        open={resolveDefect}
        onClose={markDefectResolved}
      >
        <DialogTitle>Resolve Defect</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter resolution for Defect to mark as closed.
          </DialogContentText>
          <br />
          <TextField
            autoFocus
            multiline
            required
            margin="dense"
            id="name"
            name="defectResolution"
            label="Defect Resolution"
            fullWidth
            variant="standard"
            onChange={handleResolution}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
          <Button onClick={markDefectResolved} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Cancel</Button>
          <Button onClick={resolveExistingDefect} variant="contained" color="success">Resolve</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>

    <DefectChatModal
      open={chatDefectId !== null}
      defectId={chatDefectId}
      onClose={() => setChatDefectId(null)}
      onMarkComplete={(id) => markDefectResolved(id)}
      onReportNew={() => { setChatDefectId(null); setAddDefect(true); }}
      onChanged={() => {
        if (openDefectLoaded) showOpenDefects();
        if (closedDefectLoaded) showClosedDefects();
      }}
    />

    <React.Fragment>
      <Dialog
        open={openLogin}
        onClose={openLoginPage}
      >
        <DialogTitle>Enter your details below to login to AIMS</DialogTitle>
        <DialogContent>
          <Login embedded />
        </DialogContent>
      </Dialog>
    </React.Fragment>

    <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={toastOpen}
        autoHideDuration={2500}
        onClose={handleToastClose}
        message={toastMessage}
        key={vertical + horizontal}
      />

    {localStorage.getItem('username') === null && !asset ? (
      <Login />
): null}

{!asset && !editing && !inspecting && localStorage.getItem('canSearch') === '1' ? (
  <div>
    < Search />
    {/* < AssetRegister /> */}
  </div>
): null}

{/* Logged-in user who can't search and has no asset loaded — guide them to scan,
    so they don't land on a blank screen. */}
{!asset && !editing && !inspecting && localStorage.getItem('username') !== null && localStorage.getItem('canSearch') !== '1' ? (
  <Box sx={{ maxWidth: 420, mx: 'auto', mt: 4, textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-sm)', p: 4 }}>
    <QrCode2Icon sx={{ fontSize: 56, color: 'var(--primary)' }} />
    <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text)', mt: 1 }}>Scan an asset QR code</Typography>
    <Typography variant="body2" sx={{ color: 'var(--muted)', mt: 0.5, lineHeight: 1.6 }}>
      Scan an asset's QR code with your device camera to open its details. Once you're finished, scan the next asset to view it.
    </Typography>
  </Box>
): null}


    { asset && !editing && !inspecting && localStorage.getItem('username') !== null ? (
      <Box sx={{ maxWidth: { xs: 860, md: 1100 }, margin: '0 auto' }}>
        { asset && !addAttachment ? (
      <>
        {(localStorage.getItem('canEditAsset') === '1' || localStorage.getItem('canInspectAsset') === '1') && asset.customer === '1MAINTENANCE' && (
          <TableContainer component={Paper} sx={{ mb: 1.5 }}>
            <Table sx={{ width: '100%', tableLayout: 'fixed' }} aria-label="hire link" margin="dense">
              <TableBody>
                {asset.hireItem === 'Not Setup for Hire'
                  ? <TableRow><TableCell align="center">Asset Not Setup for Hire</TableCell></TableRow>
                  : <TableRow><TableCell align="center"><Button variant="contained" size="small" href={'https://hire.1maintenance.com.au/?item='+asset.hireItem} target="_blank" rel="noopener noreferrer">Go to Hire Site for Item</Button></TableCell></TableRow>
                }
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Redesign (Direction C): single responsive asset summary card — all fields preserved */}
        <Box sx={{ background: 'var(--surface)', borderRadius: 'var(--r)', p: 2, mb: 1.5, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)' }}>
          {(() => {
            const can = (k) => localStorage.getItem(k) === '1';
            const groups = [
              [
                (can('canAddDefect') || can('canInspectAsset')) && { label: 'Report Defect', icon: <ReportProblemIcon fontSize="small" />, onClick: () => setAddDefect(true) },
                (can('canInspectAsset') || can('canUseDetailedInspection')) && { label: 'Asset Inspection', icon: <FactCheckIcon fontSize="small" />, onClick: openAssetInspection },
              ],
              [
                can('canEditAsset') && { label: 'Edit Item', icon: <EditIcon fontSize="small" />, onClick: () => setEditing(true) },
                can('canAddAttachment') && { label: 'Add Document', icon: <UploadFileIcon fontSize="small" />, onClick: () => setUploadOpen(true) },
                can('canAddAttachment') && { label: 'Add Safety Essential', icon: <HealthAndSafetyIcon fontSize="small" />, onClick: () => setSafetyUploadOpen(true) },
              ],
              [
                can('canInspectAsset') && { label: 'Job Action / C&D', icon: <BuildIcon fontSize="small" />, onClick: () => getOpenRepairs() },
                can('canInspectAsset') && { label: 'Timesheet & Parts', icon: <ScheduleIcon fontSize="small" />, onClick: openTimesheetAndParts },
                can('canInspectAsset') && { label: 'Parts Used', icon: <Inventory2Icon fontSize="small" />, onClick: () => setPartsUsedOpen(true) },
                can('canManageWorkshop') && { label: 'New Workshop Job', icon: <AddCircleOutlineIcon fontSize="small" />, onClick: () => setNewJobOpen(true) },
              ],
            ].map((g) => g.filter(Boolean)).filter((g) => g.length);
            const hasMenu = groups.length > 0;
            const hasChip = Boolean(asset.inspectionStatus);
            // Nothing to put on the top row (no permitted actions AND no status) — skip it entirely.
            if (!hasMenu && !hasChip) return null;
            return (
              <Box sx={{ display: 'flex', justifyContent: hasMenu ? 'space-between' : 'flex-end', alignItems: 'center', gap: 1 }}>
                {hasMenu ? (
                  <>
                    <IconButton size="small" aria-label="Asset actions" sx={{ ml: -0.5, p: 0.25 }} onClick={(e) => setActionMenuAnchor(e.currentTarget)}>
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={() => setActionMenuAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                      {groups.flatMap((g, gi) => [
                        gi > 0 ? <Divider key={'div-' + gi} /> : null,
                        ...g.map((a, ai) => (
                          <MenuItem key={gi + '-' + ai} onClick={() => { setActionMenuAnchor(null); a.onClick(); }}>
                            <ListItemIcon sx={{ minWidth: 34 }}>{a.icon}</ListItemIcon>
                            {a.label}
                          </MenuItem>
                        )),
                      ])}
                    </Menu>
                  </>
                ) : null}
                {hasChip ? (
                  <StatusChip domain="inspection" value={asset.inspectionStatus} dot size="md" />
                ) : null}
              </Box>
            );
          })()}

          <Box sx={{ minWidth: 0, mt: -1 }}>
            <AssetIdentity asset={asset} size="block" idStyle={{ fontSize: 21 }} />
            <Box sx={{ fontSize: 12, color: 'var(--muted)', mt: 0.5 }}>
              {[asset.customer, asset.siteLocation].filter(Boolean).join(' · ') || '—'}
            </Box>
            <OfflineCheckoutBadge />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            <KpiTile label="Cal. Expiry" value={fmtDate(asset.expiringCalibrationDate)} />
            <KpiTile label="Date Inspected" value={fmtDate(asset.lastInspectionDate)} />
            <KpiTile
              label="Next Insp. Due"
              value={fmtDate(asset.nextInspectionDate)}
              action={(localStorage.getItem('canInspectAsset') === '1' || localStorage.getItem('canUseDetailedInspection') === '1') ? (
                <Tooltip title="Start Inspection">
                  <IconButton size="small" aria-label="Start inspection" sx={{ p: 0.25, color: 'var(--primary)' }} onClick={openAssetInspection}>
                    <FactCheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px 24px', mt: 2 }}>
            <DetailField label="Serial Number" value={asset.serialNo} mono />
            <DetailField label="Type" value={asset.type} />
            <DetailField label="Item" value={asset.item} />
            <DetailField label="Manufacturer" value={asset.manufacturer} />
            <DetailField label="Model" value={asset.model} />
            <DetailField label="Max Height" value={asset.maxHeight} mono />
            <DetailField label="Capacity" value={asset.capacity} mono />
            <DetailField label="Build Date" value={fmtDate(asset.buildDate)} mono />
            <DetailField label="Site Contact">
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {asset.siteContact || '—'}
                <IconButton size="small" onClick={handleContactShow}><HelpIcon fontSize="small" /></IconButton>
              </Box>
            </DetailField>
            <DetailField label="Comments" value={asset.inspectionReason} />
          </Box>
        </Box>
      </>
    ):null}

{/* ===== Asset detail sections — rebuilt 2026-06-25. Meatballs menu (top of summary card) is the single home for all actions. The old per-section expand toggles (Defects / Documents / History) are now a single segmented control: only the sections the user can see render as segments, and the selected segment's content shows below. ===== */}
{ !addAttachment && asset && localStorage.getItem('username') !== null ? (() => {
  const can = (k) => localStorage.getItem(k) === '1';
  const sections = [
    (can('canAddDefect') || can('canInspectAsset')) && { key: 'documents', label: 'Documents' },
    { key: 'history', label: 'History' },
    (can('canAddDefect') || can('canInspectAsset')) && { key: 'defects', label: 'Defects' },
  ].filter(Boolean);
  if (sections.length === 0) return null;
  const current = sections.some((s) => s.key === activeSection) ? activeSection : sections[0].key;

  /* ---- Defects body ---- */
  const defectsBody = (
    <TableContainer component={Paper} sx={{ borderRadius: '0 0 var(--r) var(--r)' }}>
      <Table sx={{ width: '100%', tableLayout: 'fixed' }} margin="dense">
          <TableBody>
                        <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                            <TableCell align="center" onClick={() => (openDefectLoaded ? hideOpenDefects() : showOpenDefects())} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Open Defects</Typography> </TableCell>        
                        </TableRow>
                                {openDefectLoaded ? (
                                  openDefects.length === 0 ? (
                                    <TableRow style={{backgroundColor:'var(--surface)'}}><TableCell align="center" style={{color:'var(--muted)'}}>No open defects for this asset.</TableCell></TableRow>
                                  ) : openDefects.map((list, index) => (
                                    <TableRow key={index} style={{backgroundColor:'var(--surface)'}} hover>
                                      <TableCell onClick={() => setChatDefectId(list.id)} sx={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                          <Box><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Defect Added:</span> {moment(list.createdAt).format('DD-MM-YYYY hh:mm A')} <br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Reported By:</span> {list.reportedBy === null ? '---':list.reportedBy} (User: {list.addedBy})<br /> <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Defect Found:</span> {list.defectComment}</Box>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                                            { localStorage.getItem('canInspectAsset') === '1' ? (<IconButton margin="dense" variant="contained" onClick={(e) => {e.stopPropagation(); markDefectResolved(list.id)}}><Tooltip title="Mark defect as Complete"><CheckCircleOutlineIcon /></Tooltip></IconButton>): null}
                                            <DefectChatIndicator messageCount={list.messageCount} unreadCount={list.unreadCount} />
                                          </Box>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ):null}
                        <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                            <TableCell align="center" onClick={() => (closedDefectLoaded ? hideClosedDefects() : showClosedDefects())} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Closed Defects</Typography> </TableCell>
                        </TableRow>
                                {closedDefectLoaded ? (
                                  closedDefects.length === 0 ? (
                                    <TableRow style={{backgroundColor:'var(--surface)'}}><TableCell align="center" style={{color:'var(--muted)'}}>No closed defects for this asset.</TableCell></TableRow>
                                  ) : closedDefects.map((list, index) => (
                                    <TableRow key={index} style={{backgroundColor:'var(--surface)'}} hover>
                                      <TableCell onClick={() => setChatDefectId(list.id)} sx={{ cursor: 'pointer' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                          <Box><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Defect Added:</span> {moment(list.createdAt).format('DD-MM-YYYY hh:mm A')} <br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Reported By:</span> {list.reportedBy === null ? '---':list.reportedBy} (User: {list.addedBy})<br /> <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Defect Found:</span> {list.defectComment}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Defect Resolved:</span> {moment(list.resolvedAt).format('DD-MM-YYYY hh:mm A')} <br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Resolved By:</span> {list.resolvedBy}<br /> <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Defect Resolution:</span> {list.resolvedComment}</Box>
                                          <DefectChatIndicator messageCount={list.messageCount} unreadCount={list.unreadCount} />
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ):null}
          </TableBody>
      </Table>
    </TableContainer>
  );

  /* ---- Documents body ---- */
  const canAddAttachment = localStorage.getItem('canAddAttachment') === '1';
  const docColSpan = canAddAttachment ? 2 : 1;
  const documentsBody = (
    <TableContainer component={Paper} sx={{ borderRadius: '0 0 var(--r) var(--r)' }}>
      <Table sx={{ width: '100%', tableLayout: 'fixed' }} margin="dense">
          <colgroup><col />{canAddAttachment ? <col style={{ width: 52 }} /> : null}</colgroup>
          <TableBody>
                        <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                            <TableCell colSpan={docColSpan} align="center" onClick={() => (certificateDocumentLoaded ? hideCertificateDocuments() : showCertificateAttachments())} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Certificates</Typography> </TableCell>        
                        </TableRow>
                                {certificateDocumentLoaded ? (
                                  Certificates.length === 0 ? (
                                    <TableRow style={{backgroundColor:'var(--surface)'}}><TableCell colSpan={docColSpan} align="center" style={{color:'var(--muted)'}}>No certificates found.</TableCell></TableRow>
                                  ) : Certificates.map((list, index) => (
                                    <TableRow key={index} style={{backgroundColor:'var(--surface)'}}>
                                        <TableCell align="center"><AttachmentLink name={list.image_name} onClick={(e) => { e.preventDefault(); handleAttachmentOpen(list.proxyUrl); }} /></TableCell>{canAddAttachment ? (<TableCell align="center" padding="none" sx={{ width: 52 }}><IconButton size="small" onClick = {() => {handleClickOpen(list.uploadId)}} ><Tooltip title="Delete Attachment"><CancelIcon fontSize="small" /></Tooltip></IconButton></TableCell>) : null}
                                    </TableRow>
                                  ))
                                ):null}
                        <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                            <TableCell colSpan={docColSpan} align="center" onClick={() => (partsAttachmentsLoaded ? hidePartsDocuments() : showPartsAttachments())} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Parts List</Typography> </TableCell>
                        </TableRow>
                                {partsAttachmentsLoaded ? (
                                  Parts.length === 0 ? (
                                    <TableRow style={{backgroundColor:'var(--surface)'}}><TableCell colSpan={docColSpan} align="center" style={{color:'var(--muted)'}}>No parts list documents found.</TableCell></TableRow>
                                  ) : Parts.map((list, index) => (
                        <TableRow key={index} style={{backgroundColor:'var(--surface)'}}>
                            <TableCell align="center"><AttachmentLink name={list.image_name} onClick={(e) => { e.preventDefault(); handleAttachmentOpen(list.proxyUrl); }} /></TableCell>{canAddAttachment ? (<TableCell align="center" padding="none" sx={{ width: 52 }}><IconButton size="small" onClick = {() => {handleClickOpen(list.uploadId)}} ><Tooltip title="Delete Attachment"><CancelIcon fontSize="small" /></Tooltip></IconButton></TableCell>) : null}
                        </TableRow>
                                  ))
                                ):null}
                        <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                            <TableCell colSpan={docColSpan} align="center" onClick={() => (photoAttachmentsLoaded ? hidePhotoDocuments() : showPhotoAttachments())} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Media</Typography> </TableCell>
                        </TableRow>
                                {photoAttachmentsLoaded ? (
                                  Photos.length === 0 ? (
                                    <TableRow style={{backgroundColor:'var(--surface)'}}><TableCell colSpan={docColSpan} align="center" style={{color:'var(--muted)'}}>No media found.</TableCell></TableRow>
                                  ) : Photos.map((list, index) => (
                        <TableRow key={index} style={{backgroundColor:'var(--surface)'}}>
                            <TableCell align="center"><AttachmentLink name={list.image_name} onClick={(e) => { e.preventDefault(); handleAttachmentOpen(list.proxyUrl); }} /></TableCell>{canAddAttachment ? (<TableCell align="center" padding="none" sx={{ width: 52 }}><IconButton size="small" onClick = {() => {handleClickOpen(list.uploadId)}} ><Tooltip title="Delete Attachment"><CancelIcon fontSize="small" /></Tooltip></IconButton></TableCell>) : null}
                        </TableRow>
                                  ))
                                ):null}
                                  <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                        <TableCell colSpan={docColSpan} align="center" onClick={() => (safetyEssentialsLoaded ? hideSafetyEssentialsList() : showSafetyEssentialsList())} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Safety Essentials</Typography> </TableCell>
                        </TableRow>
                                {safetyEssentialsLoaded ? (
                                  (!safetyEssentials || safetyEssentials.length === 0) ? (
                                    <TableRow><TableCell colSpan={docColSpan} align="center" style={{color:'var(--muted)'}}>No safety essentials found.</TableCell></TableRow>
                                  ) : safetyEssentials.map((list, index) => (
                                    <TableRow key={index} style={{backgroundColor:'var(--surface)'}}>
                                        <TableCell align="center"><AttachmentLink name={list.image_name} isLink={list.isLink} href={list.isLink ? list.linkUrl : baseUrl + list.proxyUrl} /></TableCell>{canAddAttachment ? (<TableCell align="center" padding="none" sx={{ width: 52 }}><IconButton size="small" onClick = {() => {handleClickOpen(list.uploadId)}} ><Tooltip title="Delete Attachment"><CancelIcon fontSize="small" /></Tooltip></IconButton></TableCell>) : null}
                                    </TableRow>
                                  ))
                                ):null}
          </TableBody>
      </Table>
    </TableContainer>
  );

  /* ---- History body ---- */
  const historyBody = (
      <>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, boxShadow: 'none' }}>
                              <Table sx={{ width: '100%', tableLayout: 'fixed' }} aria-label="simple table" margin="dense">
                                <TableHead>
                                  <TableCell align="center" style={{backgroundColor:'var(--surface-2)'}} onClick={() => (inspectionHistoryLoaded ? hideInspectionHistory() : showInspectionHistory())} sx={{ cursor: 'pointer' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Inspection History</Typography>
                                  </TableCell>
                                </TableHead>
                              {asset && inspectionHistoryLoaded ? (
                              <TableBody>
                                              {inspectionHistory.length === 0 ? (
                                                <TableRow><TableCell align="center" style={{color:'var(--muted)'}}>No inspection history found.</TableCell></TableRow>
                                              ) : inspectionHistory.map((list, index) => (
                                                <TableRow key={index}  style={{backgroundColor:'var(--surface)'}}>
                                                    <TableCell align="center">
                                                      <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Inspection Date:</span> {moment(list.updatedAt).format('DD-MM-YYYY hh:mm A')} <br />
                                                      <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Status:</span> {list.inspectionStatus}<br />
                                                      <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Inspected By:</span> {list.updatedBy}<br />
                                                      <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Notes:</span> {list.inspectionReason}
                                                      {list.workshopInspectionId && (
                                                        <div style={{marginTop: 6, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap'}}>
                                                          <Button size="small" variant="outlined" onClick={() => { setInspDetailId(list.workshopInspectionId); setInspDetailOpen(true); }}>
                                                            View Checklist
                                                          </Button>
                                                          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={generatingInspPdf === list.workshopInspectionId} onClick={() => generateInspectionPdf(list.workshopInspectionId)}>
                                                            {generatingInspPdf === list.workshopInspectionId ? 'Generating...' : 'PDF Report'}
                                                          </Button>
                                                        </div>
                                                      )}
                                                    </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>):null}
                              </Table>
                            </TableContainer>
        { localStorage.getItem('canInspectAsset') === '1' ? (
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, boxShadow: 'none' }}>
                              <Table sx={{ width: '100%', tableLayout: 'fixed' }} aria-label="simple table" margin="dense">
                                <TableHead>
                                  <TableCell align="center" style={{backgroundColor:'var(--surface-2)'}} onClick={() => (timesheetsLoaded ? showTimesheetsList() : loadTimesheets())} sx={{ cursor: 'pointer' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Timesheets</Typography>
                                  </TableCell>
                                </TableHead>
                              {asset && timesheetsLoaded ? (
                              <TableBody>
                                              {timesheetList.length === 0 ? (
                                                <TableRow><TableCell align="center" style={{color:'var(--muted)'}}>No timesheets found.</TableCell></TableRow>
                                              ) : timesheetList.map((list, index) => (
                                                <TableRow key={index}  style={{backgroundColor:'var(--surface)'}}>
                                                    <TableCell onClick={() => { setFoundTimesheet( ({ ...foundTimesheet, catNo: list.catNo, assetId: list.assetId, customer: asset.customer, id: list.id, timeSpent: list.timeSpent, notes: list.notes, workPerformedOn: list.workPerformedOn, jobCard: list.jobCard, purchaseOrder: list.purchaseOrder, lineItem: list.lineItem, status: list.status, jobNumber: list.jobNumber, omnixJobCardId: list.omnixJobCardId || null, omnixSyncStatus: list.omnixSyncStatus || null }), handleTimesheetShow() )}} align="center"><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Work Performed On:</span> {moment(list.workPerformedOn).format('DD-MM-YYYY')} <br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Status:</span> {list.status}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Job Card:</span> {list.jobCard}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Purchase Order:</span> {list.purchaseOrder}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Line Item:</span> {list.lineItem}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Time Spent (Hours):</span> {list.timeSpent}<br /> <span style={{fontWeight: "bold", fontFamily: 'arial'}}>Added By:</span> {list.username}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Notes:</span> {list.notes}<br /><span style={{fontWeight: "bold", fontFamily: 'arial'}}>Parts added to Timesheet:</span> {list.hasParts}</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>):null}
                              </Table>
                            </TableContainer>
        ) : null }
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: canSeeWorkshopHistory ? 0 : '0 0 var(--r) var(--r)', boxShadow: 'none' }}>
          <Table>
          <TableHead>
          <TableCell align="center" style={{backgroundColor:'var(--surface-2)'}} onClick={showRepairsList} sx={{ cursor: 'pointer' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Job History</Typography>
          </TableCell>
        </TableHead>
      {repairListVisible ? (
        <Table sx={{ width: '100%', tableLayout: 'fixed' }} aria-label="simple table" margin="dense">           
      {repairs.length === 0 ? ( 
        <TableBody>
        <TableRow>
          <TableCell align="center">
            No job history found.
          </TableCell>
        </TableRow>
        </TableBody>
      ) : (
          <TableBody>
            {repairs?.map((r) => (
              <TableRow
                key={r.repairId}
                style={{ cursor: "pointer" }}
                onClick={() => 
                  setSelectedRepairId(
                    {assetId: asset?.assetId, 
                     repairId: r.repairId }, 
                    console.log('sri: ',selectedRepairId),
                    setRepairOpen(true),
                    setMode("view"))}
              >
                <TableCell align="center">
                  Created At: {new Date(r.createdAt).toLocaleString()} <br/> 
                  Pickup Approved By: {r.pickupApprovedBy || "-"} <br /> 
                  Return Approved By: {r.returnApprovedBy || "-"} <br /> 
                  Status: {r.status}
                </TableCell>
            </TableRow>
            ))}
          </TableBody>
      )}
        </Table>
      ) : null}
      </Table>
        </TableContainer>
        { canSeeWorkshopHistory && (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '0 0 var(--r) var(--r)', boxShadow: 'none' }}>
          <Table sx={{ width: '100%', tableLayout: 'fixed' }} margin="dense">
            <TableBody>
                    <>
                    <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                      <TableCell align="center" onClick={loadWorkshopHistory} sx={{ cursor: 'pointer' }}><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Workshop History</Typography></TableCell>
                    </TableRow>
                    {workshopHistoryOpen && (
                      <TableRow style={{backgroundColor:'var(--surface)'}}>
                        <TableCell>
                          {workshopHistoryLoading && <div style={{ textAlign: 'center', padding: 12 }}>Loading...</div>}
                          {!workshopHistoryLoading && workshopHistory.length === 0 && (
                            <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 8 }}>No workshop history for this asset.</div>
                          )}
                          {!workshopHistoryLoading && workshopHistory.map(job => (
                            <div key={job.id} style={{ borderBottom: '1px solid #eee', paddingBottom: 10, marginBottom: 10 }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {job.jobType || 'Job'} — {job.status}
                                {job.approval ? ` (${job.approval})` : ''}
                              </div>
                              <div style={{ fontSize: 12, color: '#555' }}>
                                Received: {job.dateReceived ? moment(job.dateReceived).format('DD/MM/YYYY') : '—'}
                                {job.jobAssignedTo ? ` · ${job.jobAssignedTo}` : ''}
                              </div>
                              {job.hasTeardown > 0 && (
                                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Teardown recorded</div>
                              )}
                              {job.calCount > 0 && (
                                <div style={{ fontSize: 11, color: '#555' }}>{job.calCount} calibration{job.calCount > 1 ? 's' : ''}</div>
                              )}
                              {job.certs && job.certs.length > 0 && (
                                <div style={{ marginTop: 4 }}>
                                  {job.certs.map(cert => {
                                    const isStale = cert.calUpdatedAt && new Date(cert.calUpdatedAt) > new Date(cert.generatedAt);
                                    return (
                                      <div key={cert.uploadId} style={{ fontSize: 11 }}>
                                        <span className="aims-mono" style={{ color: 'var(--primary)', fontWeight: 600 }}>{cert.certNo}</span>
                                        {' '}
                                        {isStale ? (
                                          <span style={{ color: 'var(--warn)', fontSize: 11, fontWeight: 600 }}>Certificate outdated — regenerate required</span>
                                        ) : (
                                          <a href="#" onClick={(e) => { e.preventDefault(); handleAttachmentOpen('attachment/' + cert.uploadId); }}
                                             style={{ fontSize: 11, color: 'var(--primary)' }}>
                                            Download PDF
                                          </a>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}
                    </>
            </TableBody>
          </Table>
        </TableContainer>
        ) }
      </>
  );

  const bodyFor = (key) => (key === 'documents' ? documentsBody : key === 'history' ? historyBody : defectsBody);
  const colState = { documents: docsColOpen, history: histColOpen, defects: defectsColOpen };
  const toggleCol = (key) => {
    if (key === 'documents') setDocsColOpen((o) => !o);
    else if (key === 'history') setHistColOpen((o) => !o);
    else setDefectsColOpen((o) => !o);
  };

  return (
  <div className="mb-2" style={{ marginBottom: 12 }}>
    {isDesktop ? (
      /* Desktop: 3 collapsed columns — each heading reuses the segmented-control button style. */
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))`, gap: 2, alignItems: 'start' }}>
        {sections.map((s) => (
          <Box key={s.key} sx={{ minWidth: 0 }}>
            <ToggleButton
              value={s.key}
              selected={colState[s.key]}
              onClick={() => toggleCol(s.key)}
              fullWidth
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                // Solid grey pill with dark, readable text in light mode (was a faint transparent MUI default).
                color: 'var(--text)',
                bgcolor: 'var(--border-strong)',
                borderColor: 'var(--border-strong)',
                '&:hover': { bgcolor: 'var(--border-strong)', filter: 'brightness(0.97)' },
                // Open: switch to the blue tint + primary text so the active column reads clearly and joins its body below.
                '&.Mui-selected': { bgcolor: 'var(--primary-tint)', color: 'var(--primary)', borderColor: 'var(--border-strong)' },
                '&.Mui-selected:hover': { bgcolor: 'var(--primary-tint)', filter: 'brightness(0.98)' },
                borderBottomLeftRadius: colState[s.key] ? 0 : undefined,
                borderBottomRightRadius: colState[s.key] ? 0 : undefined,
              }}
            >
              {s.label}
            </ToggleButton>
            {colState[s.key] && bodyFor(s.key)}
          </Box>
        ))}
      </Box>
    ) : (
      /* Mobile: keep the segmented control — one section's content at a time. */
      <>
        <ToggleButtonGroup
          value={current}
          exclusive
          fullWidth
          size="small"
          onChange={(e, val) => { if (val) setActiveSection(val); }}
          sx={{ '& .MuiToggleButton-root': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }}
        >
          {sections.map((s) => (
            <ToggleButton key={s.key} value={s.key} sx={{ textTransform: 'none', fontWeight: 700 }}>{s.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        {bodyFor(current)}
      </>
    )}
  </div>
  );
})() : null}





  </Box>
    ):
    <div>
        { asset && localStorage.getItem('username') === null ? (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
 {/* Logged-out minimal asset view (same fields as before, new card styling) */}
 <Box sx={{ background: 'var(--surface)', borderRadius: 'var(--r)', p: 2, mb: 1.5, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)' }}>
   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
     <Box sx={{ minWidth: 0 }}>
       <AssetIdentity asset={asset} roomy size="block" idStyle={{ fontSize: 21 }} />
       <Box sx={{ fontSize: 12, color: 'var(--muted)', mt: 0.5 }}>{[asset.item, asset.siteLocation].filter(Boolean).join(' · ') || '—'}</Box>
       <OfflineCheckoutBadge />
     </Box>
     {asset.inspectionStatus ? <StatusChip domain="inspection" value={asset.inspectionStatus} dot size="md" /> : null}
   </Box>
   <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
     <KpiTile label="Cal. Expiry" value={(asset.expiringCalibrationDateYMD === null || asset.expiringCalibrationDateYMD === '-') ? '—' : asset.expiringCalibrationDateYMD} />
     <KpiTile label="Date Inspected" value={(asset.lastInspectionDateYMD === null || asset.lastInspectionDateYMD === '-') ? '—' : asset.lastInspectionDateYMD} />
     <KpiTile label="Next Insp. Due" value={(asset.nextInspectionDateYMD === null || asset.nextInspectionDateYMD === '-') ? '—' : asset.nextInspectionDateYMD} />
   </Box>
   <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px 24px', mt: 2 }}>
     <DetailField label="Type" value={asset.type} />
     <DetailField label="Item" value={asset.item} />
     <DetailField label="Manufacturer" value={asset.manufacturer} />
     <DetailField label="Model" value={asset.model} />
   </Box>
 </Box>
    <TableContainer component={Paper}>
      <Table sx={{ width: '100%', tableLayout: 'fixed' }} aria-label="simple table" margin="dense">
                                  <TableRow style={{backgroundColor:'var(--surface-2)'}}>
                        <TableCell  align="center"><Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text)', m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Safety Essentials{ !safetyEssentialsLoaded ? ( <IconButton onClick={showSafetyEssentialsList}><ExpandMoreIcon /></IconButton>) : (<IconButton margin="dense" variant="contained" onClick={hideSafetyEssentialsList}><ExpandLessIcon /></IconButton>)}</Typography> </TableCell>
                        </TableRow>
                                {safetyEssentialsLoaded ? (
                                  (!safetyEssentials || safetyEssentials.length === 0) ? (
                                    <TableRow><TableCell align="center" style={{color:'var(--muted)'}}>No safety essentials found.</TableCell></TableRow>
                                  ) : safetyEssentials.map((list, index) => (
                                    <TableRow key={index} style={{backgroundColor:'var(--surface)'}}>
                                        <TableCell align="center"><AttachmentLink name={list.image_name} isLink={list.isLink} href={list.isLink ? list.linkUrl : baseUrl + list.proxyUrl} /></TableCell>{ localStorage.getItem('canAddAttachment') === '1' ? (<TableCell align="right" sx={{ width: 44 }}><IconButton margin="dense" variant="contained" onClick = {() => {handleClickOpen(list.uploadId)}} ><Tooltip title="Delete Attachment"><CancelIcon /></Tooltip></IconButton></TableCell>) : null}
                                    </TableRow>
                                  ))
                                ):null}
                                  </Table>
                                  </TableContainer>
                            <Button
                                margin="dense"
                                variant="contained"
                                style={{minWidth: '100%'}}
                                onClick={() => {
                                   openLoginPage()
                                }}
                            >For Full Access to certificate history and defect reporting, user login is required
                            </Button>
                            </Box>
    ):null}
    </div>
    }

{ localStorage.getItem('canEditAsset') === '1' ? (
<Dialog open={!addAttachment && editing} onClose={() => setEditing(false)} maxWidth="sm" fullWidth>
  <DialogContent sx={{ p: 0 }}>
    {editing && <Edit foundAsset={asset} onCancel={() => setEditing(false)} />}
  </DialogContent>
</Dialog>
): null}

  </div>
);
}
export default AssetView;